import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import Modal from '../../../common/modal/Modal';
import ConfirmationModal from '../../../common/modal/ConfirmationModal';
import BusRetentionScheduleEditForm from './BusRetentionScheduleEditForm';
import {
  deleteBusRetentionSchedule,
  getLastBusRetentionScheduleByRegisteredBus,
  putBusRetentionSchedule,
} from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_LOCAL } from '../../../../config/locale';
import { COMMERCIAL_STATUS } from '../../../../config/constants';

const BusRetentionScheduleActionsCell = ({
  registeredBusFleet,
  onSuccess,
  dispatchGetLastBusRetentionScheduleByRegisteredBus,
  dispatchPutBusRetentionSchedule,
  dispatchDeleteBusRetentionSchedule,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [retention, setRetention] = useState(null);

  const {
    registeredBusId,
    companyBusId,
    licensePlate,
    activeRetentionId,
    hasActiveRetentionNow,
    eventStatus,
    currentStatus,
  } = registeredBusFleet;

  const isReten =
    hasActiveRetentionNow ||
    !!activeRetentionId ||
    eventStatus === COMMERCIAL_STATUS.STANDBY.value ||
    currentStatus === COMMERCIAL_STATUS.STANDBY.value;

  if (!isReten) return null;

  const busLabel = companyBusId || licensePlate;

  const getRetention = async () => {
    const response = await dispatchGetLastBusRetentionScheduleByRegisteredBus(
      registeredBusId,
    );
    setRetention(response);
    return response;
  };

  const getBusRetentionScheduleId = (currentRetention) =>
    activeRetentionId || (currentRetention && currentRetention.id);

  const handleEditClick = async () => {
    const response = await getRetention();

    if (!getBusRetentionScheduleId(response)) {
      toastr.error('Error', 'No se encontró el retén del bus.');
      return;
    }

    setShowEditModal(true);
  };

  const handleEditClose = () => setShowEditModal(false);

  const handleEditSubmit = async (formValues) => {
    const { fromDateString, toDateString, location } = formValues;

    handleEditClose();

    const updated = await dispatchPutBusRetentionSchedule(
      getBusRetentionScheduleId(retention),
      {
        fromDateString: tzNormalizeDate({ date: fromDateString }),
        toDateString: tzNormalizeDate({ date: toDateString }),
        locationId: location.value,
        registeredBusId,
      },
    );

    if (updated && onSuccess) onSuccess();
  };

  const handleDeleteClick = async () => {
    const response = activeRetentionId ? retention : await getRetention();

    if (!getBusRetentionScheduleId(response)) {
      toastr.error('Error', 'No se encontró el retén del bus.');
      return;
    }

    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => setShowDeleteModal(false);

  const handleDeleteConfirm = async () => {
    handleDeleteClose();

    const deleted = await dispatchDeleteBusRetentionSchedule({
      busRetentionScheduleId: getBusRetentionScheduleId(retention),
    });

    if (deleted && onSuccess) onSuccess();
  };

  let editInitialValues = {};

  if (retention) {
    const { startDate, endDate, location, locationId, locationName } =
      retention;

    let locationInitialValue = null;

    if (location) {
      locationInitialValue = { value: location.id, label: location.name };
    } else if (locationId) {
      locationInitialValue = {
        value: locationId,
        label: locationName || `Ubicación ${locationId}`,
      };
    }

    editInitialValues = {
      fromDateString: tzNormalizeDate({
        date: startDate,
        format: DATE_TIME_LOCAL,
      }),
      toDateString: tzNormalizeDate({
        date: endDate,
        format: DATE_TIME_LOCAL,
      }),
      location: locationInitialValue,
    };
  }

  return (
    <div className="d-flex justify-content-center align-items-center">
      <Modal
        show={showEditModal}
        title={`Editar Retén - Bus ${busLabel}`}
        onClickClose={handleEditClose}
        body={
          <BusRetentionScheduleEditForm
            onSubmit={handleEditSubmit}
            onCancel={handleEditClose}
            initialValues={editInitialValues}
          />
        }
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Eliminar Retén"
        body={`¿Está seguro de que desea eliminar el retén del bus ${busLabel}?`}
        onClickConfirm={handleDeleteConfirm}
        onClickCancel={handleDeleteClose}
      />
      <Button
        color="info"
        outline
        className="mr-2"
        onClick={handleEditClick}
        title="Editar Retén"
      >
        <i className="fa fa-pencil" />
      </Button>
      <Button
        color="danger"
        outline
        onClick={handleDeleteClick}
        title="Eliminar Retén"
      >
        <i className="fa fa-trash" />
      </Button>
    </div>
  );
};

BusRetentionScheduleActionsCell.propTypes = {
  registeredBusFleet: PropTypes.shape({
    registeredBusId: PropTypes.number,
    companyBusId: PropTypes.string,
    licensePlate: PropTypes.string,
    activeRetentionId: PropTypes.number,
    hasActiveRetentionNow: PropTypes.bool,
    eventStatus: PropTypes.string,
    currentStatus: PropTypes.string,
  }).isRequired,
  onSuccess: PropTypes.func,
  dispatchGetLastBusRetentionScheduleByRegisteredBus: PropTypes.func.isRequired,
  dispatchPutBusRetentionSchedule: PropTypes.func.isRequired,
  dispatchDeleteBusRetentionSchedule: PropTypes.func.isRequired,
};

BusRetentionScheduleActionsCell.defaultProps = {
  onSuccess: null,
};

const mapDispatchToProps = {
  dispatchGetLastBusRetentionScheduleByRegisteredBus:
    getLastBusRetentionScheduleByRegisteredBus,
  dispatchPutBusRetentionSchedule: putBusRetentionSchedule,
  dispatchDeleteBusRetentionSchedule: deleteBusRetentionSchedule,
};

export default connect(
  null,
  mapDispatchToProps,
)(BusRetentionScheduleActionsCell);
